import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const STATUS_CFG = {
  draft:     { bg: 'bg-slate-100',  text: 'text-slate-600',  label: 'Rascunho', dot: '#94a3b8' },
  active:    { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Ativa',     dot: '#16a34a' },
  paused:    { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pausada',   dot: '#d97706' },
  completed: { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Concluída', dot: '#2563eb' },
};

const CALL_CFG = {
  pending:   { bg: 'bg-slate-100',   text: 'text-slate-500',   label: 'Pendente' },
  calling:   { bg: 'bg-blue-100',    text: 'text-blue-600',    label: 'Ligando...' },
  answered:  { bg: 'bg-green-100',   text: 'text-green-700',   label: 'Atendida' },
  no_answer: { bg: 'bg-orange-100',  text: 'text-orange-700',  label: 'Não Atendeu' },
  voicemail: { bg: 'bg-purple-100',  text: 'text-purple-700',  label: 'Caixa Postal' },
  failed:    { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Falhou' },
  completed: { bg: 'bg-teal-100',    text: 'text-teal-700',    label: 'Concluída' },
};

const OUTCOME_CFG = {
  meeting_scheduled: { label: '📅 Reunião Agendada', color: 'text-green-700' },
  not_interested:    { label: '❌ Sem Interesse',     color: 'text-red-600' },
  callback:          { label: '🔄 Retornar',          color: 'text-blue-600' },
  disqualified:      { label: '🚫 Desqualificado',    color: 'text-slate-500' },
  transferred:       { label: '👤 Transferido',        color: 'text-purple-700' },
};

const DAYS = [
  { key: 'seg', label: 'Seg' }, { key: 'ter', label: 'Ter' },
  { key: 'qua', label: 'Qua' }, { key: 'qui', label: 'Qui' },
  { key: 'sex', label: 'Sex' }, { key: 'sab', label: 'Sáb' },
  { key: 'dom', label: 'Dom' },
];

const DEFAULT_SCRIPT = `Você é {agent_name}, assistente de vendas da {company}.

Seu objetivo é qualificar o lead e agendar uma reunião de 30 minutos com nosso consultor.

REGRAS IMPORTANTES:
- Sempre se apresente como assistente/IA — nunca diga que é humano
- Nunca invente preços, prazos ou informações técnicas
- Se não souber responder, diga: "Ótima pergunta! Nosso especialista vai te explicar melhor. Posso agendar?"
- Se pedirem humano, transfira imediatamente
- Seja simpático, objetivo e natural. Máximo 4 minutos de ligação.

ROTEIRO:
1. "Olá, [Nome]! Aqui é {agent_name}, assistente da {company}. Tudo bem?"
2. "Estou ligando pois vi que você tem interesse em [produto/serviço]. Tem 2 minutos?"
3. [Se sim] Pergunte o maior desafio atual na área de [segmento]
4. Pergunte sobre urgência: "Quando precisaria de uma solução?"
5. Proponha reunião: "Posso agendar 30 min com nosso consultor. Amanhã às 15h ou quinta às 10h?"

ENCERRAMENTO:
- Reunião agendada: "Perfeito! Você receberá um convite por e-mail. Tenha um ótimo dia!"
- Sem interesse: "Entendo! Posso deixar nosso contato caso precise no futuro?"
- Transferência: "Claro! Vou conectar você agora com um de nossos consultores..."`;

function StatCard({ icon, label, value, sub, color = 'indigo' }) {
  const colors = {
    indigo: 'from-indigo-500 to-indigo-600',
    green:  'from-green-500 to-green-600',
    blue:   'from-blue-500 to-blue-600',
    teal:   'from-teal-500 to-teal-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    rose:   'from-rose-500 to-rose-600',
  };
  return (
    <Card className="border-0 shadow-sm overflow-hidden">
      <CardContent className="p-0">
        <div className={`h-1 bg-gradient-to-r ${colors[color]}`} />
        <div className="px-4 pt-3 pb-4">
          <p className="text-xs text-slate-500 mb-1">{icon} {label}</p>
          <p className="text-2xl font-bold text-slate-800 leading-none">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function DayPicker({ value = [], onChange }) {
  const toggle = (key) => {
    const updated = value.includes(key) ? value.filter(d => d !== key) : [...value, key];
    onChange(updated);
  };
  return (
    <div className="flex gap-1.5 flex-wrap">
      {DAYS.map(d => (
        <button key={d.key} type="button" onClick={() => toggle(d.key)}
          className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
            value.includes(d.key)
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300'
          }`}>
          {d.label}
        </button>
      ))}
    </div>
  );
}

function ProgressBar({ value, max, color = '#6366f1' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
      <div className="h-2 rounded-full transition-all duration-500"
        style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

const EMPTY_CAMPAIGN = {
  name: '', description: '',
  lead_list_id: '', lead_list_name: '',
  calls_per_day: 20, max_call_duration_minutes: 10,
  call_hours_start: '08:00', call_hours_end: '18:00',
  call_days: ['seg', 'ter', 'qua', 'qui', 'sex'],
  retry_no_answer: true, retry_count: 2,
  elevenlabs_voice_id: '', elevenlabs_voice_name: '',
  agent_name: 'Maya', agent_script: '',
  knowledge_urls: [],
  knowledge_files: [],
};

export default function NexoIntelligence({ api }) {
  const [settings, setSettings]         = useState({});
  const [campaigns, setCampaigns]       = useState([]);
  const [calls, setCalls]               = useState([]);
  const [stats, setStats]               = useState({});
  const [leadLists, setLeadLists]       = useState([]);
  const [saving, setSaving]             = useState(false);
  const [showSecret, setShowSecret]     = useState({ twilio_auth: false, eleven_key: false });
  const [showModal, setShowModal]       = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm]                 = useState(EMPTY_CAMPAIGN);
  const [modalTab, setModalTab]         = useState('basic');
  const [monitorId, setMonitorId]       = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('campaigns');
  const [callFilter, setCallFilter]     = useState('all');
  // Voice & AI generation
  const [voices, setVoices]             = useState([]);
  const [voicesNote, setVoicesNote]     = useState('');
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [ptBrOnly, setPtBrOnly]         = useState(true);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [briefing, setBriefing]         = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [audioLoading, setAudioLoading] = useState(null);
  const audioRef = React.useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [sRes, cRes, callRes, stRes, llRes] = await Promise.all([
        api.get('/nexo-intelligence/settings'),
        api.get('/nexo-intelligence/campaigns'),
        api.get('/nexo-intelligence/calls?limit=200'),
        api.get('/nexo-intelligence/stats'),
        api.get('/nexo-intelligence/lead-lists'),
      ]);
      setSettings(sRes.data || {});
      setCampaigns(Array.isArray(cRes.data) ? cRes.data : []);
      setCalls(Array.isArray(callRes.data) ? callRes.data : []);
      setStats(stRes.data || {});
      setLeadLists(Array.isArray(llRes.data) ? llRes.data : []);
    } catch (e) { console.error('NexoIntelligence load error', e); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const playVoicePreview = useCallback(async (voiceId) => {
    if (audioLoading === voiceId) return;
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingVoiceId === voiceId) {
      setPlayingVoiceId(null);
      return;
    }
    // Prefer the static preview_url returned from the voices list (free, no TTS quota)
    const voice = voices.find(v => v.voice_id === voiceId);
    const previewUrl = voice?.preview_url;

    setAudioLoading(voiceId);
    try {
      let audioSrc;
      if (previewUrl) {
        // Use ElevenLabs pre-generated preview mp3 directly
        audioSrc = previewUrl;
      } else {
        // Fallback: generate TTS via backend (only for account voices)
        const resp = await api.get(
          `/nexo-intelligence/voices/${voiceId}/preview`,
          { responseType: 'blob', timeout: 30000 }
        );
        audioSrc = URL.createObjectURL(resp.data);
      }
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      audio.onended = () => { setPlayingVoiceId(null); };
      audio.onerror = () => { setPlayingVoiceId(null); toast.error('Erro ao reproduzir áudio'); };
      await audio.play();
      setPlayingVoiceId(voiceId);
    } catch (e) {
      toast.error('Preview indisponível para esta voz');
    } finally {
      setAudioLoading(null);
    }
  }, [api, audioLoading, playingVoiceId, voices]);

  const loadVoices = useCallback(async (ptOnly = true) => {
    setLoadingVoices(true);
    try {
      const param = ptOnly ? '?lang=pt' : '';
      const res = await api.get(`/nexo-intelligence/voices${param}`);
      setVoices(res.data.voices || []);
      setVoicesNote(res.data.note || '');
    } catch (e) { setVoices([]); }
    finally { setLoadingVoices(false); }
  }, [api]);

  // Auto-load voices whenever the voice tab becomes active in the modal
  useEffect(() => {
    if (showModal && modalTab === 'voice') {
      loadVoices(ptBrOnly);
    }
  }, [showModal, modalTab, loadVoices]); // eslint-disable-line

  const handleFileUpload = async (e, campaignId) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Campanha ainda não salva (sem campaignId) usa o endpoint genérico de
      // extração — o backend faz a extração de verdade (PDF via pdfminer)
      // igual ao endpoint de campanha já existente, só não persiste em nada
      // ainda; o arquivo entra no payload quando a campanha for salva.
      const url = campaignId
        ? `/nexo-intelligence/campaigns/${campaignId}/knowledge/upload`
        : '/nexo-intelligence/knowledge/extract';
      const res = await api.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const entry = res.data.file;
      setForm(f => ({ ...f, knowledge_files: [...(f.knowledge_files || []), entry] }));
      toast.success(`Arquivo "${file.name}" carregado!`);
    } catch (e) { toast.error('Erro ao carregar arquivo'); }
    finally { setUploadingFile(false); }
  };

  const removeKnowledgeFile = async (fileId, campaignId) => {
    if (campaignId) {
      try { await api.delete(`/nexo-intelligence/campaigns/${campaignId}/knowledge/file/${fileId}`); }
      catch (e) {}
    }
    setForm(f => ({ ...f, knowledge_files: (f.knowledge_files || []).filter(x => x.id !== fileId) }));
  };

  const handleGenerateScript = async () => {
    if (!briefing.trim()) { toast.error('Escreva um briefing antes de gerar o roteiro'); return; }
    setGeneratingScript(true);
    try {
      const res = await api.post('/nexo-intelligence/generate-script', {
        campaign_id: editingCampaign?.id || null,
        briefing,
        agent_name: form.agent_name || 'Maya',
        company_name: settings.agent_company || '',
        knowledge_urls: (form.knowledge_urls || []).filter(u => u.trim()),
        knowledge_files_preview: (form.knowledge_files || []).map(f => f.content_preview || ''),
      });
      setForm(f => ({ ...f, agent_script: res.data.script }));
      toast.success('Roteiro gerado pela IA!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar roteiro'); }
    finally { setGeneratingScript(false); }
  };

  const openNewModal = () => {
    setEditingCampaign(null);
    setBriefing('');
    setForm({ ...EMPTY_CAMPAIGN, agent_script: DEFAULT_SCRIPT });
    setModalTab('basic');
    setShowModal(true);
  };

  const openEditModal = (c) => {
    setEditingCampaign(c);
    setBriefing('');
    setForm({ ...EMPTY_CAMPAIGN, ...c, knowledge_files: c.knowledge_files || [], knowledge_urls: c.knowledge_urls || [] });
    setModalTab('basic');
    setShowModal(true);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/nexo-intelligence/settings', settings);
      toast.success('Configurações salvas!');
    } catch (e) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleSaveCampaign = async () => {
    if (!form.name.trim()) { toast.error('Informe o nome da campanha'); return; }
    try {
      if (editingCampaign) {
        await api.put(`/nexo-intelligence/campaigns/${editingCampaign.id}`, form);
        toast.success('Campanha atualizada!');
      } else {
        await api.post('/nexo-intelligence/campaigns', form);
        toast.success('Campanha criada!');
      }
      setShowModal(false);
      loadAll();
    } catch (e) { toast.error('Erro ao salvar campanha'); }
  };

  const handleStatus = async (id, status) => {
    try {
      await api.put(`/nexo-intelligence/campaigns/${id}`, { status });
      toast.success(`Campanha ${STATUS_CFG[status]?.label?.toLowerCase()}!`);
      loadAll();
    } catch (e) { toast.error('Erro ao atualizar'); }
  };

  const handleDispatch = async (id) => {
    try {
      const res = await api.post(`/nexo-intelligence/campaigns/${id}/dispatch`);
      const n = res.data?.dispatched ?? 0;
      if (n > 0) toast.success(`${n} ligação(ões) disparada(s) agora!`);
      else toast.info(res.data?.reason || 'Nenhum lead elegível para ligar agora.');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao disparar ligações');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta campanha?')) return;
    try {
      await api.delete(`/nexo-intelligence/campaigns/${id}`);
      toast.success('Campanha removida');
      if (monitorId === id) setMonitorId(null);
      loadAll();
    } catch (e) { toast.error('Erro ao remover'); }
  };

  const isConfigured = settings.twilio_account_sid && settings.twilio_auth_token && settings.twilio_phone_number;
  const monitoredCampaign = campaigns.find(c => c.id === monitorId);
  const campaignCalls = calls.filter(c => c.campaign_id === monitorId);

  const filteredCalls = callFilter === 'all' ? calls
    : callFilter === 'transferred' ? calls.filter(c => c.transferred_to_human)
    : calls.filter(c => c.outcome === callFilter);

  const fmtDur = (sec) => sec ? `${Math.floor(sec / 60)}m ${sec % 60}s` : '—';
  const fmtDate = (d) => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  return (
    <div className="p-6 space-y-5 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
            style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>🤖</div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">SDR NexoIntelligence</h1>
            <p className="text-xs text-slate-500">IA de prospecção por voz — ligações automáticas com Maya</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isConfigured
            ? <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">✅ Integração Ativa</Badge>
            : <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">⚠️ Configure Twilio</Badge>}
          <Button size="sm" variant="outline" onClick={loadAll} className="text-xs">↺ Atualizar</Button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard icon="📋" label="Campanhas"  value={stats.total_campaigns ?? 0} color="indigo" />
        <StatCard icon="🟢" label="Ativas"     value={stats.active_campaigns ?? 0} color="green" />
        <StatCard icon="📞" label="Ligações"   value={stats.total_calls ?? 0} color="blue" />
        <StatCard icon="✅" label="Atendidas"  value={`${stats.answer_rate ?? 0}%`} sub={`${stats.answered_calls ?? 0} ligações`} color="teal" />
        <StatCard icon="📅" label="Reuniões"   value={stats.meetings_scheduled ?? 0} color="purple" />
        <StatCard icon="👤" label="Transferidas" value={stats.transferred_to_human ?? 0} sub="→ humano" color="orange" />
        <StatCard icon="⏱️" label="Total Minutos" value={stats.total_minutes ?? 0} sub="min consumidos" color="rose" />
      </div>

      {/* ── Main Tabs ── */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="campaigns">📋 Campanhas</TabsTrigger>
          <TabsTrigger value="monitor">
            🔍 Monitor {monitoredCampaign ? `— ${monitoredCampaign.name}` : ''}
          </TabsTrigger>
          <TabsTrigger value="calls">📞 Histórico de Ligações</TabsTrigger>
          <TabsTrigger value="ai-global">🤖 IA Global</TabsTrigger>
        </TabsList>

        {/* ══ CAMPANHAS ══ */}
        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Campanhas de Prospecção</h3>
              <p className="text-xs text-slate-500">Cada campanha tem sua própria IA, voz, lista de leads e agenda</p>
            </div>
            <Button onClick={openNewModal} className="btn-primary shrink-0">+ Nova Campanha</Button>
          </div>

          {!isConfigured && (
            <div className="p-3 rounded-lg border border-orange-200 bg-orange-50 text-xs text-orange-700">
              ⚠️ Configure as integrações Twilio e ElevenLabs na aba <strong>Integrações</strong> antes de ativar campanhas.
            </div>
          )}

          {campaigns.length === 0 ? (
            <div className="text-center py-14 text-slate-400">
              <div className="text-5xl mb-3">📋</div>
              <p className="font-medium text-slate-500">Nenhuma campanha criada</p>
              <p className="text-sm mt-1">Crie sua primeira campanha de ligações para começar</p>
              <Button onClick={openNewModal} className="btn-primary mt-4">+ Criar primeira campanha</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {campaigns.map(c => {
                const st = STATUS_CFG[c.status] || STATUS_CFG.draft;
                const answerPct = c.called > 0 ? ((c.answered / c.called) * 100).toFixed(0) : 0;
                const convPct   = c.answered > 0 ? ((c.meetings_scheduled / c.answered) * 100).toFixed(0) : 0;
                const progress  = c.total_leads > 0 ? Math.round((c.called / c.total_leads) * 100) : 0;
                return (
                  <Card key={c.id} className={`border shadow-sm transition-all ${monitorId === c.id ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-slate-200'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Title row */}
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: st.dot }} />
                            <h4 className="font-semibold text-slate-800">{c.name}</h4>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>{st.label}</span>
                            {c.agent_name && c.agent_name !== 'Maya' && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-600">🤖 {c.agent_name}</span>
                            )}
                            {c.elevenlabs_voice_name && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-600">🎙️ {c.elevenlabs_voice_name}</span>
                            )}
                          </div>
                          {c.description && <p className="text-xs text-slate-500 mb-2">{c.description}</p>}

                          {/* Config line */}
                          <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-3">
                            {c.lead_list_name && <span>📋 {c.lead_list_name}</span>}
                            <span>📞 {c.calls_per_day}/dia</span>
                            <span>⏱️ máx {c.max_call_duration_minutes}min</span>
                            <span>🕐 {c.call_hours_start}–{c.call_hours_end}</span>
                            {c.retry_no_answer && <span>🔄 {c.retry_count}x retry</span>}
                          </div>

                          {/* Progress bar */}
                          {c.total_leads > 0 && (
                            <div className="mb-3">
                              <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Progresso: {c.called}/{c.total_leads} leads</span>
                                <span>{progress}%</span>
                              </div>
                              <ProgressBar value={c.called} max={c.total_leads} color={st.dot} />
                            </div>
                          )}

                          {/* Stats row */}
                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                            {[
                              { l: 'Ligações', v: c.called },
                              { l: 'Atendidas', v: `${c.answered} (${answerPct}%)` },
                              { l: 'Reuniões', v: `${c.meetings_scheduled} (${convPct}%)` },
                              { l: 'Sem Interesse', v: c.not_interested ?? 0 },
                              { l: 'Transferidas', v: c.transferred_to_human ?? 0 },
                              { l: 'Minutos', v: c.total_minutes ?? 0 },
                            ].map(m => (
                              <div key={m.l} className="bg-slate-50 rounded-lg px-2 py-1.5 text-center">
                                <p className="text-xs text-slate-400 truncate">{m.l}</p>
                                <p className="text-sm font-bold text-slate-700">{m.v}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-col gap-1.5 shrink-0 min-w-[100px]">
                          {c.status === 'draft' && (
                            <Button size="sm" className="btn-primary text-xs w-full" onClick={() => handleStatus(c.id, 'active')}>▶ Ativar</Button>
                          )}
                          {c.status === 'active' && (
                            <>
                              <Button size="sm" variant="outline" className="text-xs w-full" onClick={() => handleDispatch(c.id)}>📞 Disparar agora</Button>
                              <Button size="sm" variant="outline" className="text-xs w-full" onClick={() => handleStatus(c.id, 'paused')}>⏸ Pausar</Button>
                            </>
                          )}
                          {c.status === 'paused' && (
                            <Button size="sm" className="btn-primary text-xs w-full" onClick={() => handleStatus(c.id, 'active')}>▶ Retomar</Button>
                          )}
                          <Button size="sm" variant="outline" className="text-xs w-full"
                            onClick={() => {
                              if (monitorId === c.id) {
                                setMonitorId(null);
                              } else {
                                setMonitorId(c.id);
                                setActiveMainTab('monitor');
                              }
                            }}>
                            {monitorId === c.id ? '✕ Fechar Monitor' : '🔍 Monitor'}
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs w-full" onClick={() => openEditModal(c)}>✏️ Editar</Button>
                          <Button size="sm" variant="ghost" className="text-xs w-full text-red-400 hover:text-red-600" onClick={() => handleDelete(c.id)}>🗑 Excluir</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ══ MONITOR ══ */}
        <TabsContent value="monitor" className="space-y-4">
          {!monitoredCampaign ? (
            <div className="text-center py-14 text-slate-400">
              <p>Selecione uma campanha na aba Campanhas e clique em 🔍 Monitor</p>
            </div>
          ) : (
            <>
              {/* Campaign header */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-semibold text-slate-800 text-lg">{monitoredCampaign.name}</h3>
                  <p className="text-xs text-slate-500">
                    Atualizado em {fmtDate(monitoredCampaign.updated_at)} · {monitoredCampaign.agent_name || 'Maya'} · {monitoredCampaign.lead_list_name || 'Lista não definida'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {monitoredCampaign.status === 'active' && (
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => handleStatus(monitoredCampaign.id, 'paused')}>⏸ Pausar</Button>
                  )}
                  {monitoredCampaign.status === 'paused' && (
                    <Button size="sm" className="btn-primary text-xs" onClick={() => handleStatus(monitoredCampaign.id, 'active')}>▶ Retomar</Button>
                  )}
                  <Button size="sm" variant="outline" className="text-xs" onClick={loadAll}>↺ Atualizar</Button>
                </div>
              </div>

              {/* Big progress */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex justify-between text-sm font-medium text-slate-700 mb-2">
                    <span>Progresso da campanha</span>
                    <span>{monitoredCampaign.called} / {monitoredCampaign.total_leads || '?'} leads contactados</span>
                  </div>
                  <ProgressBar
                    value={monitoredCampaign.called}
                    max={monitoredCampaign.total_leads || monitoredCampaign.called || 1}
                    color="#6366f1"
                  />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    {[
                      { l: '📞 Ligações realizadas', v: monitoredCampaign.called, c: '#6366f1' },
                      { l: '✅ Atendidas',           v: monitoredCampaign.answered, c: '#16a34a' },
                      { l: '📅 Reuniões',            v: monitoredCampaign.meetings_scheduled, c: '#9333ea' },
                      { l: '👤 Transferidas p/ humano', v: monitoredCampaign.transferred_to_human ?? 0, c: '#ea580c' },
                    ].map(m => (
                      <div key={m.l} className="text-center rounded-xl p-3" style={{ background: m.c + '14' }}>
                        <p className="text-xs text-slate-500 mb-1">{m.l}</p>
                        <p className="text-2xl font-bold" style={{ color: m.c }}>{m.v}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Outcomes breakdown */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  { l: 'Reuniões Agendadas', v: monitoredCampaign.meetings_scheduled, icon: '📅', color: 'green' },
                  { l: 'Sem Interesse',      v: monitoredCampaign.not_interested ?? 0, icon: '❌', color: 'red' },
                  { l: 'Callback Solicitado',v: monitoredCampaign.callback_requested ?? 0, icon: '🔄', color: 'blue' },
                  { l: 'Transferidas',       v: monitoredCampaign.transferred_to_human ?? 0, icon: '👤', color: 'orange' },
                  { l: 'Total Minutos',      v: monitoredCampaign.total_minutes ?? 0, icon: '⏱️', color: 'slate' },
                ].map(m => (
                  <Card key={m.l} className="border-0 shadow-sm">
                    <CardContent className="pt-3 pb-3 px-3 text-center">
                      <p className="text-xl mb-1">{m.icon}</p>
                      <p className="text-xs text-slate-500">{m.l}</p>
                      <p className="text-xl font-bold text-slate-800">{m.v}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Live calls for this campaign */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Ligações desta campanha</CardTitle>
                  <CardDescription className="text-xs">{campaignCalls.length} registradas</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {campaignCalls.length === 0 ? (
                    <p className="text-center text-sm text-slate-400 py-8">Nenhuma ligação registrada ainda</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                          <tr>
                            <th className="px-4 py-2 text-left">Lead</th>
                            <th className="px-4 py-2 text-left">Empresa</th>
                            <th className="px-4 py-2 text-left">Telefone</th>
                            <th className="px-4 py-2 text-left">Status</th>
                            <th className="px-4 py-2 text-left">Resultado</th>
                            <th className="px-4 py-2 text-left">Duração</th>
                            <th className="px-4 py-2 text-left">Transferido</th>
                            <th className="px-4 py-2 text-left">Data</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {campaignCalls.map(call => {
                            const cs = CALL_CFG[call.status] || CALL_CFG.pending;
                            return (
                              <tr key={call.id} className={`hover:bg-slate-50 ${call.transferred_to_human ? 'bg-orange-50' : ''}`}>
                                <td className="px-4 py-2 font-medium text-slate-800">{call.lead_name || '—'}</td>
                                <td className="px-4 py-2 text-slate-500">{call.lead_company || '—'}</td>
                                <td className="px-4 py-2 font-mono text-slate-500">{call.lead_phone || '—'}</td>
                                <td className="px-4 py-2">
                                  <span className={`px-2 py-0.5 rounded-full ${cs.bg} ${cs.text}`}>{cs.label}</span>
                                </td>
                                <td className="px-4 py-2">
                                  {OUTCOME_CFG[call.outcome]
                                    ? <span className={OUTCOME_CFG[call.outcome].color}>{OUTCOME_CFG[call.outcome].label}</span>
                                    : '—'}
                                </td>
                                <td className="px-4 py-2 text-slate-500">{fmtDur(call.duration_seconds)}</td>
                                <td className="px-4 py-2">
                                  {call.transferred_to_human
                                    ? <span className="text-orange-600 font-medium">👤 Sim{call.transfer_reason ? ` — ${call.transfer_reason}` : ''}</span>
                                    : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="px-4 py-2 text-slate-400">{fmtDate(call.created_at)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ══ HISTÓRICO DE LIGAÇÕES ══ */}
        <TabsContent value="calls" className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-semibold text-slate-800">Histórico de Ligações</h3>
              <p className="text-xs text-slate-500">{filteredCalls.length} ligações</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: 'all', l: 'Todas' },
                { v: 'transferred', l: '👤 Transferidas' },
                { v: 'meeting_scheduled', l: '📅 Reunião' },
                { v: 'not_interested', l: '❌ Sem Interesse' },
                { v: 'callback', l: '🔄 Callback' },
              ].map(f => (
                <button key={f.v} onClick={() => setCallFilter(f.v)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    callFilter === f.v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-500 border-slate-200'
                  }`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>

          {filteredCalls.length === 0 ? (
            <div className="text-center py-14 text-slate-400">
              <div className="text-5xl mb-3">📞</div>
              <p>Nenhuma ligação encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 text-left">Lead</th>
                    <th className="px-4 py-3 text-left">Empresa</th>
                    <th className="px-4 py-3 text-left">Campanha</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Resultado</th>
                    <th className="px-4 py-3 text-left">Duração</th>
                    <th className="px-4 py-3 text-left">Transferido</th>
                    <th className="px-4 py-3 text-left">Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCalls.map(call => {
                    const cs = CALL_CFG[call.status] || CALL_CFG.pending;
                    return (
                      <tr key={call.id} className={`hover:bg-slate-50 ${call.transferred_to_human ? 'bg-orange-50' : ''}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{call.lead_name || '—'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{call.lead_company || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{call.campaign_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${cs.bg} ${cs.text}`}>{cs.label}</span>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {OUTCOME_CFG[call.outcome]
                            ? <span className={OUTCOME_CFG[call.outcome].color}>{OUTCOME_CFG[call.outcome].label}</span>
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{fmtDur(call.duration_seconds)}</td>
                        <td className="px-4 py-3 text-xs">
                          {call.transferred_to_human
                            ? <span className="text-orange-600 font-semibold">👤 Sim</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(call.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ══ IA GLOBAL ══ */}
        <TabsContent value="ai-global" className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">🤖 Persona e Roteiro Padrão</CardTitle>
              <CardDescription>Configurações padrão para todas as campanhas. Podem ser sobrescritas em cada campanha.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nome da IA</Label>
                  <Input placeholder="Maya" value={settings.agent_name || ''}
                    onChange={e => setSettings({ ...settings, agent_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nome da Empresa</Label>
                  <Input placeholder="Place Logistics" value={settings.agent_company || ''}
                    onChange={e => setSettings({ ...settings, agent_company: e.target.value })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs">Objetivo padrão</Label>
                  <Input placeholder="Qualificar leads e agendar reunião com consultor"
                    value={settings.agent_objective || ''}
                    onChange={e => setSettings({ ...settings, agent_objective: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Roteiro Padrão (System Prompt)</Label>
                <Textarea rows={12} className="font-mono text-xs"
                  placeholder={DEFAULT_SCRIPT}
                  value={settings.agent_script || ''}
                  onChange={e => setSettings({ ...settings, agent_script: e.target.value })} />
                <p className="text-xs text-slate-400">Use {'{'+'agent_name'+'}'} e {'{'+'company'+'}'} como variáveis dinâmicas</p>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={saving} className="btn-primary px-8">
              {saving ? 'Salvando...' : '💾 Salvar Roteiro'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* ══ MODAL: Criar / Editar Campanha ══ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-800">
                {editingCampaign ? `✏️ Editar — ${editingCampaign.name}` : '+ Nova Campanha'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 text-xl">✕</button>
            </div>

            {/* Modal tabs */}
            <div className="flex border-b border-slate-100 px-6 gap-1">
              {[
                { id: 'basic',    label: '📋 Básico' },
                { id: 'leads',    label: '👥 Lead Source' },
                { id: 'schedule', label: '⏰ Agenda' },
                { id: 'voice',    label: '🎙️ Voz & IA' },
              ].map(t => (
                <button key={t.id} onClick={() => setModalTab(t.id)}
                  className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                    modalTab === t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

              {/* ── Básico ── */}
              {modalTab === 'basic' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Nome da Campanha *</Label>
                    <Input placeholder="Ex: Prospecção Logística Q1 2025"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Descrição</Label>
                    <Textarea rows={2} placeholder="Objetivo e público-alvo desta campanha..."
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })} />
                  </div>
                </div>
              )}

              {/* ── Lead Source ── */}
              {modalTab === 'leads' && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Lista de Leads (fonte dos números)</Label>
                    <Select value={form.lead_list_id || '__none__'}
                      onValueChange={v => {
                        const found = leadLists.find(l => l.id === v);
                        setForm({ ...form, lead_list_id: v === '__none__' ? '' : v, lead_list_name: found?.name || '', total_leads: found?.prospects_count || 0 });
                      }}>
                      <SelectTrigger><SelectValue placeholder="Selecione uma lista..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Selecione uma lista —</SelectItem>
                        {leadLists.map(l => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.name} ({l.prospects_count} leads)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">A IA usará o nome e telefone de cada lead desta lista</p>
                  </div>
                  {form.lead_list_id && form.lead_list_id !== '__none__' && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                      ✅ <strong>{form.lead_list_name}</strong> selecionada — {form.total_leads} leads disponíveis
                    </div>
                  )}
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-1">
                    <p className="font-semibold">Como funciona:</p>
                    <p>• A IA percorre cada lead da lista na ordem e faz a ligação</p>
                    <p>• Leads sem telefone cadastrado são pulados automaticamente</p>
                    <p>• Leads já contatados nesta campanha não são repetidos</p>
                    <p>• Para criar listas, use o menu <strong>Prospecções → Busca de Emails</strong></p>
                  </div>
                </div>
              )}

              {/* ── Agenda ── */}
              {modalTab === 'schedule' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Ligações por dia</Label>
                      <Input type="number" min={1} max={500}
                        value={form.calls_per_day}
                        onChange={e => setForm({ ...form, calls_per_day: parseInt(e.target.value) || 20 })} />
                      <p className="text-xs text-slate-400">Limite diário de ligações desta campanha</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Duração máxima (min)</Label>
                      <Input type="number" min={1} max={60}
                        value={form.max_call_duration_minutes}
                        onChange={e => setForm({ ...form, max_call_duration_minutes: parseInt(e.target.value) || 10 })} />
                      <p className="text-xs text-slate-400">A ligação encerra após este tempo</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Horário início</Label>
                      <Input type="time" value={form.call_hours_start}
                        onChange={e => setForm({ ...form, call_hours_start: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Horário fim</Label>
                      <Input type="time" value={form.call_hours_end}
                        onChange={e => setForm({ ...form, call_hours_end: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Dias de operação</Label>
                    <DayPicker value={form.call_days || []} onChange={v => setForm({ ...form, call_days: v })} />
                  </div>
                  <div className="space-y-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <Label className="text-xs font-medium">Retentativas (não atendeu)</Label>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={form.retry_no_answer}
                          onChange={e => setForm({ ...form, retry_no_answer: e.target.checked })}
                          className="rounded" />
                        Retentar quando não atender
                      </label>
                      {form.retry_no_answer && (
                        <div className="flex items-center gap-2">
                          <Input type="number" min={1} max={5} className="w-16 text-xs"
                            value={form.retry_count}
                            onChange={e => setForm({ ...form, retry_count: parseInt(e.target.value) || 2 })} />
                          <span className="text-xs text-slate-500">tentativas</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cost estimate */}
                  {form.calls_per_day > 0 && (
                    <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100 text-xs text-indigo-700 space-y-1">
                      <p className="font-semibold">📊 Estimativa de custo diário</p>
                      <p>Taxa de atendimento ~30%: {Math.round(form.calls_per_day * 0.3)} ligações atendidas</p>
                      <p>Custo estimado: R$ {((form.calls_per_day * 0.7 * 0.13) + (form.calls_per_day * 0.3 * (form.max_call_duration_minutes * 0.26 * 0.6))).toFixed(2)}/dia</p>
                      <p>Mensal (20 dias): R$ {((form.calls_per_day * 0.7 * 0.13) + (form.calls_per_day * 0.3 * (form.max_call_duration_minutes * 0.26 * 0.6)) * 20).toFixed(2)}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── Voz & IA ── */}
              {modalTab === 'voice' && (
                <div className="space-y-5">
                  <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                    💡 Campos vazios usam a configuração global das Integrações / IA Global
                  </div>

                  {/* Nome da IA + Voz */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Nome da IA nesta campanha</Label>
                      <Input placeholder="Maya (padrão)"
                        value={form.agent_name}
                        onChange={e => setForm({ ...form, agent_name: e.target.value })} />
                      <p className="text-xs text-slate-400">Como a IA se apresenta nesta campanha</p>
                    </div>

                    {/* Voice picker with player */}
                    <div className="space-y-2 md:col-span-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <Label className="text-xs font-medium">Voz ElevenLabs</Label>
                        <div className="flex items-center gap-2">
                          <div className="flex rounded-md border border-slate-200 overflow-hidden text-xs">
                            <button type="button"
                              onClick={() => { setPtBrOnly(true); loadVoices(true); }}
                              className={`px-2.5 py-1 transition-colors ${ptBrOnly ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                              🇧🇷 PT-BR
                            </button>
                            <button type="button"
                              onClick={() => { setPtBrOnly(false); loadVoices(false); }}
                              className={`px-2.5 py-1 transition-colors border-l border-slate-200 ${!ptBrOnly ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                              🌐 Todas
                            </button>
                          </div>
                          <button type="button" onClick={() => loadVoices(ptBrOnly)} disabled={loadingVoices}
                            className="text-xs text-indigo-500 hover:text-indigo-700 disabled:opacity-40">
                            {loadingVoices ? '⏳' : '↺ Atualizar'}
                          </button>
                        </div>
                      </div>

                      {/* Selected voice pill */}
                      {form.elevenlabs_voice_id && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs">
                          <span className="text-indigo-600 font-medium">✅ Voz selecionada: {form.elevenlabs_voice_name || form.elevenlabs_voice_id}</span>
                          <button type="button" onClick={() => setForm(f => ({ ...f, elevenlabs_voice_id: '', elevenlabs_voice_name: '' }))}
                            className="ml-auto text-slate-400 hover:text-red-500">✕ Remover</button>
                        </div>
                      )}

                      {/* Voice cards grid */}
                      {loadingVoices ? (
                        <div className="text-center py-6 text-slate-400 text-sm">⏳ Carregando vozes...</div>
                      ) : voices.length === 0 ? (
                        <div className="text-center py-6 text-slate-400 text-sm">Nenhuma voz encontrada</div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                          {voices.map(v => {
                            const isSelected = form.elevenlabs_voice_id === v.voice_id;
                            const isPlaying  = playingVoiceId === v.voice_id;
                            const isLoading  = audioLoading === v.voice_id;
                            return (
                              <div key={v.voice_id}
                                onClick={() => setForm(f => ({ ...f, elevenlabs_voice_id: v.voice_id, elevenlabs_voice_name: v.name }))}
                                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-all select-none ${
                                  isSelected
                                    ? 'border-indigo-400 bg-indigo-50 shadow-sm'
                                    : 'border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50'
                                }`}>
                                {/* Play button */}
                                <button type="button"
                                  onClick={e => { e.stopPropagation(); playVoicePreview(v.voice_id); }}
                                  disabled={isLoading}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm transition-colors ${
                                    isPlaying  ? 'bg-red-100 text-red-600 hover:bg-red-200' :
                                    isLoading  ? 'bg-slate-100 text-slate-400' :
                                    isSelected ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200' :
                                                 'bg-slate-100 text-slate-500 hover:bg-indigo-100 hover:text-indigo-600'
                                  }`}
                                  title={isPlaying ? 'Parar' : 'Ouvir amostra'}>
                                  {isLoading ? '⏳' : isPlaying ? '⏹' : '▶'}
                                </button>

                                {/* Name + badges */}
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-medium truncate ${isSelected ? 'text-indigo-700' : 'text-slate-700'}`}>
                                    {v.name}
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {v.owned === true ? (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 rounded px-1 py-0">
                                        ✓ Sua conta
                                      </span>
                                    ) : v.owned === false ? (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0" title="Esta voz é do catálogo ElevenLabs e pode não estar disponível para TTS no seu plano atual">
                                        📚 Catálogo
                                      </span>
                                    ) : null}
                                  </div>
                                </div>

                                {/* Selected check */}
                                {isSelected && <span className="text-indigo-500 text-sm shrink-0">✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {voicesNote && <p className="text-xs text-slate-400">{voicesNote}</p>}
                    </div>
                  </div>

                  {/* ── Knowledge Base ── */}
                  <div className="border border-slate-200 rounded-xl p-4 space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700">🧠 Base de Conhecimento da Maya</h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Anexe materiais sobre o produto/empresa. A Maya usa isso para criar o roteiro e responder melhor.
                      </p>
                    </div>

                    {/* URLs */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">🌐 URLs de referência</Label>
                      {(form.knowledge_urls || []).map((url, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input value={url} placeholder="https://minhaempresa.com.br/produto"
                            className="text-xs flex-1"
                            onChange={e => {
                              const updated = [...(form.knowledge_urls || [])];
                              updated[idx] = e.target.value;
                              setForm(f => ({ ...f, knowledge_urls: updated }));
                            }} />
                          <button type="button"
                            onClick={() => setForm(f => ({ ...f, knowledge_urls: (f.knowledge_urls || []).filter((_, i) => i !== idx) }))}
                            className="text-red-400 hover:text-red-600 px-2 text-sm">✕</button>
                        </div>
                      ))}
                      <button type="button"
                        onClick={() => setForm(f => ({ ...f, knowledge_urls: [...(f.knowledge_urls || []), ''] }))}
                        className="text-xs text-indigo-600 hover:underline">+ Adicionar URL</button>
                    </div>

                    {/* File upload */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">📎 Arquivos de produto (PDF, TXT, DOCX)</Label>
                      {(form.knowledge_files || []).length > 0 && (
                        <div className="space-y-1">
                          {(form.knowledge_files || []).map(f => (
                            <div key={f.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
                              <span className="text-lg">{f.ext === 'pdf' ? '📄' : f.ext === 'txt' ? '📝' : '📋'}</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-slate-700 truncate">{f.name}</p>
                                <p className="text-xs text-slate-400">{f.size ? `${(f.size / 1024).toFixed(1)} KB` : ''}</p>
                              </div>
                              <button type="button"
                                onClick={() => removeKnowledgeFile(f.id, editingCampaign?.id)}
                                className="text-red-400 hover:text-red-600 text-xs">✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                      <label className={`flex items-center gap-2 px-3 py-2 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-indigo-300 transition-colors text-xs text-slate-500 ${uploadingFile ? 'opacity-50 pointer-events-none' : ''}`}>
                        <span>{uploadingFile ? '⏳ Carregando...' : '📁 Clique para anexar arquivo'}</span>
                        <input type="file" className="hidden" accept=".pdf,.txt,.md,.csv,.docx"
                          onChange={e => handleFileUpload(e, editingCampaign?.id)}
                          disabled={uploadingFile} />
                      </label>
                    </div>
                  </div>

                  {/* ── Roteiro ── */}
                  <div className="border border-slate-200 rounded-xl p-4 space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700">✍️ Roteiro da Campanha</h4>
                      <p className="text-xs text-slate-500 mt-0.5">Escreva manualmente ou deixe a Maya criar com base no briefing</p>
                    </div>

                    {/* Briefing for AI generation */}
                    <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100 space-y-2">
                      <Label className="text-xs font-semibold text-indigo-700">🤖 Gerar roteiro com IA</Label>
                      <Textarea rows={3} className="text-xs bg-white"
                        placeholder="Descreva o produto, público-alvo e objetivo da campanha. Ex: 'Produto de gestão logística para empresas de transporte com 10-100 veículos. Objetivo: agendar demo com diretor de operações. Principal dor: planilhas manuais e falta de rastreio em tempo real.'"
                        value={briefing}
                        onChange={e => setBriefing(e.target.value)} />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-indigo-600">
                          A Maya vai usar o briefing + arquivos + URLs acima para criar o roteiro
                        </p>
                        <Button size="sm" className="btn-primary text-xs"
                          onClick={handleGenerateScript} disabled={generatingScript || !briefing.trim()}>
                          {generatingScript ? '⏳ Gerando...' : '✨ Gerar Roteiro com IA'}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">Roteiro final (editável)</Label>
                      <Textarea rows={10} className="font-mono text-xs"
                        placeholder="O roteiro gerado pela IA aparecerá aqui. Você pode editar livremente antes de salvar."
                        value={form.agent_script}
                        onChange={e => setForm({ ...form, agent_script: e.target.value })} />
                      <p className="text-xs text-slate-400">Analise e ajuste o roteiro conforme necessário antes de ativar a campanha</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center px-6 py-4 border-t border-slate-100">
              <div className="flex gap-1">
                {['basic','leads','schedule','voice'].map((t, i) => (
                  <span key={t} onClick={() => setModalTab(t)}
                    className={`w-2 h-2 rounded-full cursor-pointer transition-colors ${
                      modalTab === t ? 'bg-indigo-600' : 'bg-slate-200 hover:bg-slate-300'
                    }`} />
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button className="btn-primary" onClick={handleSaveCampaign}>
                  {editingCampaign ? '✅ Salvar Alterações' : '🚀 Criar Campanha'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
